import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Briefcase, 
  MapPin, 
  Building2, 
  Search, 
  Filter, 
  Sparkles, 
  ArrowRight, 
  ExternalLink, 
  Bookmark, 
  ChevronRight, 
  ChevronLeft,
  Loader2, 
  RefreshCw, 
  Heart,
  X,
  Globe,
  ChevronDown,
  SlidersHorizontal,
  ArrowUpDown,
  LayoutGrid,
  List,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";

const ADZUNA_APP_ID = import.meta.env.VITE_ADZUNA_APP_ID;
const ADZUNA_APP_KEY = import.meta.env.VITE_ADZUNA_APP_KEY;
const REED_API_KEY = import.meta.env.VITE_REED_API_KEY;

const getCompanyGradient = (companyName: string): string => {
  const gradients = [
    'from-pink-500 to-rose-500',
    'from-purple-600 to-indigo-600',
    'from-blue-500 to-cyan-500',
    'from-teal-500 to-emerald-500',
    'from-amber-500 to-orange-500',
    'from-red-500 to-pink-500',
    'from-fuchsia-600 to-pink-600',
    'from-violet-600 to-purple-600',
    'from-sky-500 to-blue-500',
    'from-emerald-500 to-green-500',
  ];
  let hash = 0;
  const name = companyName || '';
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

const CompanyLogo = ({ logoUrl, companyName, size = 'md' }: { logoUrl?: string; companyName: string; size?: 'sm' | 'md' | 'lg' }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string | undefined>(logoUrl);

  useEffect(() => {
    setResolvedLogoUrl(logoUrl);
    setError(false);
    setLoaded(false);
  }, [logoUrl]);

  useEffect(() => {
    // If we have a logoUrl and it hasn't errored out yet, do nothing
    if (logoUrl && !error) return;

    // If error is true but we already tried resolving it (resolvedLogoUrl is different from logoUrl), do nothing to avoid infinite loops
    if (error && resolvedLogoUrl !== logoUrl) return;

    if (!companyName || companyName === 'N/A' || companyName.trim() === '') return;

    let isMounted = true;
    const fetchLogo = async () => {
      try {
        // Clean company name slightly if it contains extra location info or weird chars
        const cleanName = companyName
          .replace(/\(.*\)/g, '')
          .replace(/ltd|inc|co|corp|solutions|group/gi, '')
          .trim();

        if (!cleanName) return;

        const response = await fetch(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(cleanName)}`);
        if (!response.ok) return;
        const data = await response.json();
        
        if (data && data.length > 0 && data[0].domain) {
          const domain = data[0].domain;
          if (isMounted) {
            setError(false);
            setLoaded(false);
            setResolvedLogoUrl(`https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=128`);
          }
        }
      } catch (err) {
        console.warn('Failed to resolve company logo dynamically:', err);
      }
    };

    fetchLogo();

    return () => {
      isMounted = false;
    };
  }, [logoUrl, companyName, error]);

  const containerClasses = size === 'lg' 
    ? 'w-16 h-16 rounded-xl shadow-inner' 
    : 'w-12 h-12 rounded-xl shadow-inner';

  const textClasses = size === 'lg'
    ? 'text-2xl'
    : 'text-lg';

  const initial = companyName ? companyName.charAt(0).toUpperCase() : 'J';

  return (
    <div className={`${containerClasses} border border-border overflow-hidden flex-shrink-0 relative bg-white`}>
      {resolvedLogoUrl && !error && (
        <img
          src={resolvedLogoUrl}
          alt={`${companyName} logo`}
          className={`absolute inset-0 w-full h-full object-contain bg-white p-1 transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
      {(!resolvedLogoUrl || error || !loaded) && (
        <div className={`w-full h-full bg-gradient-to-br ${getCompanyGradient(companyName)} flex items-center justify-center`}>
          <span className={`${textClasses} font-extrabold text-white select-none shadow-sm`}>
            {initial}
          </span>
        </div>
      )}
    </div>
  );
};

type JobSource = 'adzuna' | 'reed' | 'jobicy' | 'remotive';

interface Job {
  id: string;
  job_title: string;
  company: string;
  location: string;
  description: string;
  apply_url: string;
  relevance_score: number;
  created_at: string;
  salary_min?: number;
  salary_max?: number;
  source: JobSource;
  logo_url?: string;
  category?: string;
}

interface LocationSuggestion {
  display: string;
  lat: string;
  lon: string;
  countryCode: string;
}

interface JobBoardProps {
  userId?: string;
  userCV: string;
  onTailor: (job: Job) => void;
}

type FilterType = 'live' | 'saved' | 'applied';

export const SOURCE_CONFIG: Record<JobSource, { label: string; className: string }> = {
  adzuna: { label: 'Adzuna', className: 'text-blue-500 border-blue-500/20 bg-blue-500/5' },
  reed: { label: 'Reed', className: 'text-indigo-500 border-indigo-500/20 bg-indigo-500/5' },
  jobicy: { label: 'Jobicy', className: 'text-emerald-500 border-emerald-500/20 bg-emerald-500/5' },
  remotive: { label: 'Remotive', className: 'text-rose-500 border-rose-500/20 bg-rose-500/5' }
};

export const JobListItem = ({ job, isSaving, isFavorite, toggleFavorite, onTailor, setSelectedJob }: any) => {
  return (
        <motion.div
                            key={job.id}
                            layout
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setSelectedJob(job)}
                            className="group bg-white dark:bg-slate-900/50 border border-border hover:border-cyan-500/50 rounded-2xl p-5 transition-all shadow-sm hover:shadow-md cursor-pointer flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative overflow-hidden"
                          >
                            <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                            
                            <div className="flex items-center gap-4 flex-1 min-w-0 z-10">
                              <CompanyLogo logoUrl={job.logo_url} companyName={job.company} size="sm" />
                              <div className="min-w-0 flex-1">
                                <h3 className="text-base font-bold text-foreground mb-1 truncate pr-4 group-hover:text-cyan-500 transition-colors">
                                  {job.job_title}
                                </h3>
                                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1 font-medium text-foreground/80">
                                    {job.company}
                                  </span>
                                  {job.location && (
                                    <span className="flex items-center gap-1">
                                      <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                      <MapPin className="w-3 h-3 opacity-70" />
                                      {job.location}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                    Posted {new Date(job.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                              </div>
                            </div>
    
                            <div className="flex items-center gap-3 w-full md:w-auto z-10 justify-between md:justify-end">
                              <div className="flex items-center gap-2">
                                {job.relevance_score > 0 ? (
                                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-bold">
                                    <Sparkles className="w-3 h-3" />
                                    {job.relevance_score}%
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-500/10 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                                    No CV
                                  </div>
                                )}
                                {job.source && SOURCE_CONFIG[job.source] && (
                                  <div className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${SOURCE_CONFIG[job.source].className}`}>
                                    {SOURCE_CONFIG[job.source].label}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  disabled={isSaving}
                                  onClick={(e) => toggleFavorite(job, e)}
                                  className={`h-8 w-8 rounded-full transition-colors ${isFavorite ? 'text-cyan-500 bg-cyan-500/10' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                                >
                                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />}
                                </Button>
                                <Button 
                                  size="sm"
                                  className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-semibold px-4 h-8 text-xs flex items-center gap-1.5 transition-all shadow-sm hover:shadow"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onTailor(job);
                                  }}
                                >
                                  Tailor CV
                                  <ArrowRight className="w-3.5 h-3.5" />
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full border-border hover:bg-muted font-semibold px-4 h-8 text-xs flex items-center gap-1.5 transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(job.apply_url, '_blank');
                                  }}
                                >
                                  Go to Listing
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </motion.div>
  );
};


export const JobBoard = ({ userId, userCV, onTailor }: JobBoardProps) => {
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeFilter, setActiveFilter] = useState<FilterType>('live');
  const [selectedSource, setSelectedSource] = useState<JobSource | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem("alvaru_job_search") ?? "");
  const [locationQuery, setLocationQuery] = useState(() => localStorage.getItem("alvaru_job_location") ?? "London");
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem("alvaru_job_country") ?? "gb");
  
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLocLoading, setIsLocLoading] = useState(false);
  const [showLocSuggestions, setShowLocSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [savingFav, setSavingFav] = useState<string | null>(null);

  const [workModeFilter, setWorkModeFilter] = useState<Set<'remote' | 'onsite'>>(new Set());
  const [workModeOpen, setWorkModeOpen] = useState(false);
  
  const [datePostedFilter, setDatePostedFilter] = useState<'any' | 'month' | 'week' | '24h'>('any');
  const [datePostedOpen, setDatePostedOpen] = useState(false);
  const datePostedRef = useRef<HTMLDivElement>(null);

  const [sortByMatch, setSortByMatch] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const workModeRef = useRef<HTMLDivElement>(null);

  // Modal State
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  const extractKeywords = (text: string) => {
    if (!text) return "Software Engineer";
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const potentialTitle = lines[1] || lines[0] || "Software Engineer";
    return potentialTitle.length > 50 ? potentialTitle.substring(0, 50) : potentialTitle;
  };

  const calculateRelevance = (jobTitle: string, jobDesc: string, cv: string) => {
    if (!cv || !cv.trim()) return 0;
    const stopWords = new Set(['the','and','for','with','to','of','in','at','on','by','is','are','was','were','be','been','have','has','had','can','will','would','could','should','from','this','that','these','those']);
    const extractKeywordsArr = (text: string) => {
      const words = text.toLowerCase().match(/[a-z]{3,}/g) || [];
      return words.filter(w => !stopWords.has(w));
    };
    const cvKeywords = new Set(extractKeywordsArr(cv));
    const jobKeywords = extractKeywordsArr(jobDesc + " " + jobTitle);
    if (jobKeywords.length === 0) return 20;
    let matches = 0;
    jobKeywords.forEach(keyword => {
      if (cvKeywords.has(keyword)) matches++;
    });
    const rawScore = Math.floor((matches / jobKeywords.length) * 100);
    return Math.min(99, Math.max(20, rawScore));
  };

  const isJobMatchingQuery = (jobTitle: string, jobDesc: string, query: string, category?: string): boolean => {
    const q = query.toLowerCase().trim();
    if (!q) return true;

    const title = jobTitle.toLowerCase();
    const desc = jobDesc.toLowerCase();
    const cat = (category || '').toLowerCase();

    // Split words
    const words = q.split(/\s+/).filter(w => w.length > 2);
    if (words.length === 0) return true;

    // 1. Direct phrase matches
    if (title.includes(q)) return true;
    if (words.every(w => title.includes(w))) return true;

    // 2. Cross-matching: Title contains any search word (e.g. "media" or "buyer")
    if (words.some(w => title.includes(w))) return true;

    // 3. Domain-level matching for Marketing & Advertising & Media & Sales
    const isMarketingQuery = words.some(w => 
      ['marketing', 'manager', 'media', 'buyer', 'seo', 'ppc', 'sem', 'social', 'brand', 'content', 'advertis', 'pr', 'creative', 'sales', 'growth', 'copywrit'].includes(w)
    );

    if (isMarketingQuery) {
      // If it explicitly belongs to a Marketing/Sales/Writing category, allow it!
      if (cat.includes('marketing') || cat.includes('sales') || cat.includes('writing')) {
        // Ensure it is not excluded by title
        const excludeKeywords = ['software', 'engineer', 'finance', 'accounting', 'tax', 'payroll', 'hr', 'recruiting', 'talent', 'support', 'it', 'developer', 'qa', 'scrum', 'agile', 'legal', 'office', 'admin'];
        const hasExclude = excludeKeywords.some(kw => title.includes(kw));
        if (!hasExclude) return true;
      }

      const marketingKeywords = ['marketing', 'seo', 'ppc', 'sem', 'social', 'brand', 'advertis', 'pr', 'communications', 'growth', 'copywrit', 'content', 'digital assort', 'creative', 'ads', 'media', 'buyer', 'sales'];
      const excludeKeywords = ['software', 'engineer', 'finance', 'accounting', 'tax', 'payroll', 'hr', 'recruiting', 'talent', 'support', 'it', 'developer', 'qa', 'scrum', 'agile', 'legal', 'office', 'admin', 'medical', 'clinical', 'nurse', 'doctor', 'lab', 'diagnostics', 'infra', 'operations lead', 'renewals'];

      // If the job title has a marketing keyword AND description mentions any of the search words
      const hasMarketingTitle = marketingKeywords.some(kw => title.includes(kw));
      const descHasAnyWord = words.some(w => desc.includes(w));

      if (hasMarketingTitle && descHasAnyWord) {
        // Ensure it is not excluded
        const hasExclude = excludeKeywords.some(kw => title.includes(kw));
        if (!hasExclude) return true;
      }
    }

    // 4. Chef & Kitchen domain matching
    const isChefQuery = words.some(w => ['chef', 'cook', 'kitchen', 'sous', 'commis', 'pastry', 'baker'].includes(w));
    if (isChefQuery) {
      if (cat.includes('hospitality') || cat.includes('food') || cat.includes('restaurant')) {
        return true;
      }
      const chefKeywords = ['chef', 'cook', 'kitchen', 'sous', 'commis', 'catering', 'food', 'restaurant', 'gastronomy', 'pastry', 'baker', 'culinary'];
      return chefKeywords.some(kw => title.includes(kw));
    }

    // 5. Fallback description matching
    const firstWord = words[0];
    if (title.includes(firstWord)) return true;
    if (words.some(w => title.includes(w)) && words.every(w => desc.includes(w))) return true;

    return false;
  };

  const formatPostedDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const diffMs = Date.now() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  // ─── SOURCE BADGE CONFIG ───────────────────────────────────────────────────
  const SOURCE_CONFIG: Record<JobSource, { label: string; className: string }> = {
    adzuna:  { label: 'Adzuna',  className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
    reed:    { label: 'Reed',    className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
    jobicy:  { label: 'Jobicy',  className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
    remotive:{ label: 'Remotive',className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  };

  // ─── DEDUPLICATION HELPER ──────────────────────────────────────────────────
  const deduplicateJobs = (allJobs: Job[]): Job[] => {
    const seen = new Set<string>();
    return allJobs.filter(job => {
      const key = `${job.job_title.toLowerCase().trim().replace(/\s+/g, ' ')}_${job.company.toLowerCase().trim().replace(/\s+/g, ' ')}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // ─── REED FETCHER ──────────────────────────────────────────────────────────
  const fetchReedJobs = async (query: string, location: string): Promise<Job[]> => {
    try {
      let sanitizedLocation = location.trim();
      // Remove country suffixes that confuse Reed's UK-only search engine
      sanitizedLocation = sanitizedLocation.replace(/,?\s*(uk|united kingdom|gb|great britain)$/i, '').trim();
      
      // Normalize common regional queries to specific city names that Reed's API expects
      if (sanitizedLocation.toLowerCase().includes('london')) {
        sanitizedLocation = 'London';
      } else if (sanitizedLocation.toLowerCase().includes('manchester')) {
        sanitizedLocation = 'Manchester';
      } else if (sanitizedLocation.toLowerCase().includes('birmingham')) {
        sanitizedLocation = 'Birmingham';
      }

      // Uses Vite dev proxy /proxy-reed → https://www.reed.co.uk (auth injected server-side)
      const url = `/proxy-reed/api/1.0/search?keywords=${encodeURIComponent(query)}&locationName=${encodeURIComponent(sanitizedLocation)}&resultsToTake=50`;
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.results || []).map((j: any): Job => ({
        id: `reed_${j.jobId}`,
        job_title: j.jobTitle || '',
        company: j.employerName || '',
        location: j.locationName || location,
        description: j.jobDescription?.replace(/<\/?[^>]+(>|$)/g, '') || '',
        apply_url: j.jobUrl || `https://www.reed.co.uk/jobs/${j.jobId}`,
        relevance_score: calculateRelevance(j.jobTitle || '', j.jobDescription || '', userCV),
        created_at: (() => {
          if (!j.date) return new Date().toISOString();
          try {
            const parts = j.date.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const parsedDate = new Date(year, month, day);
              if (!isNaN(parsedDate.getTime())) {
                return parsedDate.toISOString();
              }
            }
            const standardDate = new Date(j.date);
            return !isNaN(standardDate.getTime()) ? standardDate.toISOString() : new Date().toISOString();
          } catch {
            return new Date().toISOString();
          }
        })(),
        salary_min: j.minimumSalary || undefined,
        salary_max: j.maximumSalary || undefined,
        source: 'reed',
        logo_url: j.employerProfileImageUrl,
      }));
    } catch (err) {
      console.error('[Alvaru] Reed fetch error:', err);
      return [];
    }
  };

  // ─── JOBICY FETCHER ────────────────────────────────────────────────────────
  // ─── JOBICY FETCHER ────────────────────────────────────────────────────────
  const fetchJobicyJobs = async (query: string, countryCode: string = 'gb'): Promise<Job[]> => {
    try {
      // Map 'gb' to 'uk' because Jobicy uses 'uk' for Great Britain
      const mappedGeo = countryCode.toLowerCase() === 'gb' ? 'uk' : countryCode.toLowerCase();
      
      // Uses Vite dev proxy /proxy-jobicy → https://jobicy.com
      const url = `/proxy-jobicy/api/v2/remote-jobs?count=50&geo=${encodeURIComponent(mappedGeo)}`;
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      const allJobs = (data.jobs || []).map((j: any): Job => {
        const rawGeo = j.jobGeo || 'Remote';
        return {
          id: `jobicy_${j.id}`,
          job_title: j.jobTitle || '',
          company: j.companyName || '',
          location: rawGeo,
          description: j.jobDescription?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>') || '',
          apply_url: j.url || '',
          relevance_score: calculateRelevance(j.jobTitle || '', j.jobDescription || '', userCV),
          created_at: j.pubDate ? new Date(j.pubDate).toISOString() : new Date().toISOString(),
          source: 'jobicy',
          logo_url: j.companyLogo,
          category: (j.jobIndustry || []).join(', '),
        };
      });

      // Post-filter to strictly exclude remote restrictions that mismatch the target country
      const filteredJobs = allJobs.filter((job: Job) => {
        const geo = job.location.toLowerCase().trim();
        if (mappedGeo === 'uk') {
          // If it explicitly restricts to US/Canada/France/etc. and we are UK, reject it
          if (geo.includes('us') || geo.includes('united states') || geo.includes('canada') || geo.includes('france') || geo.includes('germany') || geo.includes('america')) {
            if (geo.includes('uk') || geo.includes('united kingdom') || geo.includes('europe') || geo.includes('emea')) {
              return true;
            }
            return false;
          }
          // Allow jobs open to UK, EMEA, Europe, Worldwide, Anywhere, generic Remote
          return (
            geo.includes('uk') || 
            geo.includes('united kingdom') || 
            geo.includes('gb') ||
            geo.includes('emea') || 
            geo.includes('europe') || 
            geo.includes('worldwide') || 
            geo.includes('anywhere') ||
            geo === 'remote' ||
            geo === ''
          );
        }
        return geo.includes(mappedGeo) || geo.includes('worldwide') || geo.includes('anywhere') || geo === 'remote' || geo === '';
      });

      // Client-side strict keyword filter (Jobicy has no server-side search)
      let keywordFiltered = filteredJobs;
      if (query.trim()) {
        keywordFiltered = filteredJobs.filter(job =>
          isJobMatchingQuery(job.job_title, job.description, query, job.category)
        );
      }

      // Format location beautifully with Remote label at the end
      return keywordFiltered.map(job => ({
        ...job,
        location: job.location.toLowerCase().includes('remote') ? job.location : `Remote (${job.location})`
      }));
    } catch (err) {
      console.error('[Alvaru] Jobicy fetch error:', err);
      return [];
    }
  };

  // ─── REMOTIVE FETCHER ──────────────────────────────────────────────────────
  const fetchRemotiveJobs = async (query: string, countryCode: string = 'gb'): Promise<Job[]> => {
    try {
      const mappedGeo = countryCode.toLowerCase() === 'gb' ? 'uk' : countryCode.toLowerCase();
      const url = `https://remotive.com/api/remote-jobs${query ? `?search=${encodeURIComponent(query)}&limit=50` : '?limit=50'}`;
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      const allJobs = (data.jobs || []).map((j: any): Job => {
        const rawGeo = j.candidate_required_location || 'Remote';
        return {
          id: `remotive_${j.id}`,
          job_title: j.title || '',
          company: j.company_name || '',
          location: rawGeo,
          description: j.description?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ') || '',
          apply_url: j.url || '',
          relevance_score: calculateRelevance(j.title || '', j.description || '', userCV),
          created_at: j.publication_date ? new Date(j.publication_date).toISOString() : new Date().toISOString(),
          source: 'remotive',
          logo_url: j.company_logo,
          category: j.category || '',
        };
      });

      // Post-filter to strictly exclude remote restrictions that mismatch the target country
      const geoFilteredJobs = allJobs.filter((job: Job) => {
        const geo = job.location.toLowerCase().trim();
        if (mappedGeo === 'uk') {
          // If it explicitly restricts to US/Canada/France/etc. and we are UK, reject it
          if (geo.includes('us') || geo.includes('united states') || geo.includes('canada') || geo.includes('france') || geo.includes('germany') || geo.includes('america')) {
            if (geo.includes('uk') || geo.includes('united kingdom') || geo.includes('europe') || geo.includes('emea')) {
              return true;
            }
            return false;
          }
          // Allow jobs open to UK, EMEA, Europe, Worldwide, Anywhere, generic Remote
          return (
            geo.includes('uk') || 
            geo.includes('united kingdom') || 
            geo.includes('gb') ||
            geo.includes('emea') || 
            geo.includes('europe') || 
            geo.includes('worldwide') || 
            geo.includes('anywhere') ||
            geo === 'remote' ||
            geo === ''
          );
        }
        return geo.includes(mappedGeo) || geo.includes('worldwide') || geo.includes('anywhere') || geo === 'remote' || geo === '';
      });

      // Client-side strict keyword filter (Remotive's API search parameter is extremely fuzzy/broken)
      let keywordFiltered = geoFilteredJobs;
      if (query.trim()) {
        keywordFiltered = geoFilteredJobs.filter(job =>
          isJobMatchingQuery(job.job_title, job.description, query, job.category)
        );
      }

      // Format location beautifully with Remote label at the end
      return keywordFiltered.map(job => ({
        ...job,
        location: job.location.toLowerCase().includes('remote') ? job.location : `Remote (${job.location})`
      }));
    } catch {
      return [];
    }
  };

  const loadFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from("saved_jobs")
        .select("job_id")
        .eq("user_id", userId);
      
      if (error) throw error;
      setFavorites(new Set(data.map(f => f.job_id)));
    } catch (err) {
      console.error("Error loading favorites:", err);
    }
  }, [userId]);

  const fetchSavedJobs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("saved_jobs")
        .select(`
          job_id,
          job_recommendations (*)
        `)
        .eq("user_id", userId);
      
      if (error) throw error;

      const mappedJobs: Job[] = data
        .filter(f => f.job_recommendations)
        .map(f => ({
          ...f.job_recommendations,
          id: f.job_id
        }));

      setJobs(mappedJobs.sort((a, b) => b.relevance_score - a.relevance_score));
      setHasMore(false);
    } catch (err) {
      console.error("Error fetching saved jobs:", err);
      toast.error("Failed to load saved jobs");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchJobs = useCallback(async (query?: string, loc?: string, country?: string, page: number = 1) => {
    if (activeFilter === 'saved') {
      await fetchSavedJobs();
      return;
    }

    if (!ADZUNA_APP_ID || !ADZUNA_APP_KEY) {
      toast.error("Adzuna API keys missing.");
      return;
    }

    setLoading(true);
    let finalQuery = query !== undefined ? query : searchQuery;
    const finalLocation = loc !== undefined ? loc : locationQuery;
    let finalCountry = country !== undefined ? country.toLowerCase() : countryCode.toLowerCase();
    if (!finalCountry || finalCountry === 'null' || finalCountry === 'undefined') {
      finalCountry = 'gb';
    }

    if (localStorage.getItem("alvaru_job_search") === null && !finalQuery && userCV) {
      finalQuery = extractKeywords(userCV);
      setSearchQuery(finalQuery);
      localStorage.setItem("alvaru_job_search", finalQuery);
    }

    try {
      // ─── ADZUNA (unchanged) ────────────────────────────────────────────────
      const adzunaPromise = fetch(
        `https://api.adzuna.com/v1/api/jobs/${finalCountry}/search/${page}?app_id=${ADZUNA_APP_ID}&app_key=${ADZUNA_APP_KEY}&results_per_page=50&what=${encodeURIComponent(finalQuery || "")}&where=${encodeURIComponent(finalLocation || "")}&content-type=application/json`,
        { headers: { 'Accept': 'application/json' } }
      ).then(async (response) => {
        if (!response.ok) throw new Error("Failed to fetch jobs");
        const data = await response.json();
        const mappedJobs: Job[] = (data.results || []).map((j: any) => ({
          id: String(j.id || Math.random().toString(36).substring(7)),
          job_title: (j.title || "").replace(/<\/?[^>]+(>|$)/g, ""),
          company: j.company?.display_name || 'N/A',
          location: j.location?.display_name || 'Remote',
          description: (j.description || "").replace(/<\/?[^>]+(>|$)/g, ""),
          apply_url: j.redirect_url || "",
          relevance_score: calculateRelevance(j.title || "", j.description || "", userCV),
          created_at: j.created || new Date().toISOString(),
          salary_min: j.salary_min || undefined,
          salary_max: j.salary_max || undefined,
          source: 'adzuna' as JobSource,
          logo_url: j.company?.logo_url,
        }));
        return { jobs: mappedJobs, count: mappedJobs.length };
      });

      // ─── ADDITIONAL SOURCES (only on page 1 to avoid duplication) ─────────
      // Fetch all sources in parallel on Page 1 to ensure individual source filters work correctly.
      const additionalPromises = page === 1
        ? [
            fetchReedJobs(finalQuery, finalLocation).then(jobs => ({ jobs, count: jobs.length })),
            fetchJobicyJobs(finalQuery, finalCountry).then(jobs => ({ jobs, count: jobs.length })),
            fetchRemotiveJobs(finalQuery, finalCountry).then(jobs => ({ jobs, count: jobs.length })),
          ]
        : [];

      const [adzunaResult, ...additionalResults] = await Promise.allSettled([
        adzunaPromise,
        ...additionalPromises,
      ]);

      const adzunaJobs: Job[] = adzunaResult.status === 'fulfilled' ? adzunaResult.value.jobs : [];
      const adzunaCount = adzunaResult.status === 'fulfilled' ? adzunaResult.value.count : 0;
      if (adzunaResult.status === 'rejected') {
        console.error("Adzuna Fetch Error:", adzunaResult.reason);
      }

      const additionalJobs: Job[] = additionalResults
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => (r as PromiseFulfilledResult<{ jobs: Job[]; count: number }>).value.jobs);

      const allJobs = deduplicateJobs([...adzunaJobs, ...additionalJobs]);
      allJobs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setJobs(allJobs);
      setHasMore(adzunaCount === 50);

      if (listContainerRef.current) {
        listContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error("Adzuna Fetch Error:", err);
      toast.error("Could not fetch live jobs for this region.");
    } finally {
      setLoading(false);
    }
  }, [userCV, ADZUNA_APP_ID, ADZUNA_APP_KEY, locationQuery, searchQuery, countryCode, activeFilter, fetchSavedJobs]);

  const fetchLocationSuggestions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setLocationSuggestions([]);
      return;
    }
    setIsLocLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setLocationSuggestions(data.map((item: any) => ({
        display: item.display_name,
        lat: item.lat,
        lon: item.lon,
        countryCode: item.address?.country_code || 'gb'
      })));
      setShowLocSuggestions(true);
    } catch (error) {
      console.error('Location fetch failed:', error);
    } finally {
      setIsLocLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeFilter === 'live') {
      fetchJobs(undefined, undefined, undefined, currentPage);
    } else if (activeFilter === 'saved') {
      fetchSavedJobs();
    }
    if (userId) loadFavorites();
  }, [activeFilter, userId, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLocSuggestions(false);
      }
      if (datePostedRef.current && !datePostedRef.current.contains(event.target as Node)) {
        setDatePostedOpen(false);
      }
      if (workModeRef.current && !workModeRef.current.contains(event.target as Node)) {
        setWorkModeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedJob) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedJob]);

  const updateSearchQuery = (val: string) => {
    setSearchQuery(val);
    localStorage.setItem("alvaru_job_search", val);
  };

  const handleManualLocationChange = (val: string) => {
    setLocationQuery(val);
    localStorage.setItem("alvaru_job_location", val);
    fetchLocationSuggestions(val);
  };

  const selectLocationSuggestion = (suggestion: LocationSuggestion) => {
    const simplified = suggestion.display.split(',')[0].trim();
    setLocationQuery(simplified);
    localStorage.setItem("alvaru_job_location", simplified);
    
    const newCode = suggestion.countryCode.toLowerCase();
    setCountryCode(newCode);
    localStorage.setItem("alvaru_job_country", newCode);

    setShowLocSuggestions(false);
    setLocationSuggestions([]);
    
    setCurrentPage(1);
    fetchJobs(searchQuery, simplified, newCode, 1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    setCurrentPage(newPage);
  };

  const getPageNumbers = () => {
    const range: number[] = [];
    const maxVisible = 5;
    
    let start = Math.max(1, currentPage - 2);
    let end = hasMore ? currentPage + 2 : currentPage;
    
    if (end - start + 1 < maxVisible && hasMore) {
      if (start === 1) {
        end = Math.max(end, start + maxVisible - 1);
      } else {
        start = Math.max(1, end - maxVisible + 1);
      }
    }
    
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const toggleFavorite = async (job: Job, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent modal from opening
    if (!userId) {
      toast.error("Please sign in to save jobs");
      return;
    }

    const isFav = favorites.has(job.id);
    setSavingFav(job.id);

    try {
      if (isFav) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", userId)
          .eq("job_id", job.id);
        if (error) throw error;
        
        const newFavs = new Set(favorites);
        newFavs.delete(job.id);
        setFavorites(newFavs);
        
        if (activeFilter === 'saved') {
          setJobs(prev => prev.filter(j => j.id !== job.id));
        }
        
        toast.success("Removed from favorites");
      } else {
        const { error: jobErr } = await supabase
          .from("job_recommendations")
          .upsert({
            id: job.id,
            user_id: userId,
            job_title: job.job_title,
            company: job.company,
            location: job.location,
            description: job.description,
            apply_url: job.apply_url,
            relevance_score: job.relevance_score
          });
        
        if (jobErr) throw jobErr;

        const { error: favErr } = await supabase
          .from("saved_jobs")
          .insert({
            user_id: userId,
            job_id: job.id
          });
        
        if (favErr) throw favErr;

        const newFavs = new Set(favorites);
        newFavs.add(job.id);
        setFavorites(newFavs);
        toast.success("Saved to favorites!");
      }
    } catch (err: any) {
      console.error("Favorite toggle error:", err);
      toast.error(err.message || "Failed to save job");
    } finally {
      setSavingFav(null);
    }
  };

  const isRemoteJob = (job: Job): boolean => {
    const loc = (job.location || '').toLowerCase();
    const title = (job.job_title || '').toLowerCase();
    const desc = (job.description || '').toLowerCase();
    return loc.includes('remote') || title.includes('remote') || desc.includes('remote work') || desc.includes('work from home') || desc.includes('fully remote') || loc === '';
  };

  const filteredBySource = selectedSource === 'all'
    ? jobs
    : jobs.filter(j => j.source === selectedSource);

  const filteredByWorkMode = workModeFilter.size === 0
    ? filteredBySource
    : filteredBySource.filter(j => {
        const remote = isRemoteJob(j);
        if (workModeFilter.has('remote') && workModeFilter.has('onsite')) return true;
        if (workModeFilter.has('remote')) return remote;
        if (workModeFilter.has('onsite')) return !remote;
        return true;
      });

  const filteredByDate = filteredByWorkMode.filter(j => {
    if (datePostedFilter === 'any') return true;
    const date = new Date(j.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (datePostedFilter === '24h') return diffDays <= 1;
    if (datePostedFilter === 'week') return diffDays <= 7;
    if (datePostedFilter === 'month') return diffDays <= 30;
    return true;
  });

  const filteredJobs = sortByMatch
    ? [...filteredByDate].sort((a, b) => b.relevance_score - a.relevance_score)
    : filteredByDate;

  return (
    <div className="flex-1 flex flex-col h-full bg-[#FAF9F6] dark:bg-[#0A0F1E] transition-colors overflow-hidden relative">
      <div className="p-8 pb-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2 flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-cyan-500" />
              Job Search
            </h1>
            <p className="text-muted-foreground text-sm max-w-xl">
              {activeFilter === 'live' ? "Personalized recommendations based on your master CV and skills." : "Your bookmarked opportunities."}
            </p>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); setShowLocSuggestions(false); setCurrentPage(1); fetchJobs(undefined, undefined, undefined, 1); }} className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Job title or skills..." 
                className="pl-10 w-[200px] bg-white dark:bg-slate-900 border-border"
                value={searchQuery}
                onChange={(e) => updateSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="relative" ref={dropdownRef}>
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Location..." 
                className="pl-10 w-[200px] bg-white dark:bg-slate-900 border-border pr-10"
                value={locationQuery}
                onChange={(e) => handleManualLocationChange(e.target.value)}
                onFocus={() => locationSuggestions.length > 0 && setShowLocSuggestions(true)}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {isLocLoading ? (
                   <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                ) : (
                   <span className="text-[10px] font-bold text-muted-foreground opacity-50 uppercase">{countryCode}</span>
                )}
              </div>
              
              <AnimatePresence>
                {showLocSuggestions && locationSuggestions.length > 0 && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-border rounded-xl shadow-2xl overflow-hidden max-h-[300px] overflow-y-auto"
                  >
                    {locationSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-3 hover:bg-muted cursor-pointer text-xs transition-colors border-b border-border/50 last:border-0 flex items-start gap-2"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectLocationSuggestion(suggestion);
                        }}
                      >
                        <Globe className="w-3.5 h-3.5 mt-0.5 text-cyan-500 shrink-0" />
                        <div className="flex flex-col">
                          <span className="font-bold line-clamp-1">{suggestion.display.split(',')[0]}</span>
                          <span className="text-[10px] text-muted-foreground line-clamp-1">{suggestion.display}</span>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Button type="submit" disabled={loading} className="bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </Button>
            <Button 
                type="button"
                variant="outline" 
                size="icon" 
                className="bg-white dark:bg-slate-900 border-border"
                onClick={() => { setCurrentPage(1); fetchJobs(undefined, undefined, undefined, 1); }}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </form>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 border-b border-border/50 pb-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0">
              <Badge 
                variant={activeFilter === 'live' ? "secondary" : "outline"} 
                onClick={() => setActiveFilter('live')}
                className={`px-4 py-1.5 rounded-full cursor-pointer transition-all duration-200 ${activeFilter === 'live' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20 font-semibold' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Live Feed
              </Badge>
              <Badge 
                variant={activeFilter === 'saved' ? "secondary" : "outline"} 
                onClick={() => setActiveFilter('saved')}
                className={`px-4 py-1.5 rounded-full cursor-pointer transition-all duration-200 ${activeFilter === 'saved' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20 font-semibold' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Saved
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground mr-1 uppercase tracking-wider opacity-60">Source Filter:</span>
              <Badge 
                variant={selectedSource === 'all' ? "secondary" : "outline"} 
                onClick={() => setSelectedSource('all')}
                className={`px-3 py-1 cursor-pointer transition-all duration-200 rounded-full ${selectedSource === 'all' ? 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20 font-medium' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                All
              </Badge>
              <Badge 
                variant={selectedSource === 'adzuna' ? "secondary" : "outline"} 
                onClick={() => setSelectedSource('adzuna')}
                className={`px-3 py-1 cursor-pointer transition-all duration-200 rounded-full ${selectedSource === 'adzuna' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20 font-medium' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Adzuna
              </Badge>
              <Badge 
                variant={selectedSource === 'reed' ? "secondary" : "outline"} 
                onClick={() => setSelectedSource('reed')}
                className={`px-3 py-1 cursor-pointer transition-all duration-200 rounded-full ${selectedSource === 'reed' ? 'bg-orange-500/10 text-orange-500 border-orange-500/20 font-medium' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Reed
              </Badge>
              <Badge 
                variant={selectedSource === 'jobicy' ? "secondary" : "outline"} 
                onClick={() => setSelectedSource('jobicy')}
                className={`px-3 py-1 cursor-pointer transition-all duration-200 rounded-full ${selectedSource === 'jobicy' ? 'bg-purple-500/10 text-purple-500 border-purple-500/20 font-medium' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Jobicy
              </Badge>
              <Badge 
                variant={selectedSource === 'remotive' ? "secondary" : "outline"} 
                onClick={() => setSelectedSource('remotive')}
                className={`px-3 py-1 cursor-pointer transition-all duration-200 rounded-full ${selectedSource === 'remotive' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 font-medium' : 'text-muted-foreground hover:bg-muted border-border'}`}
              >
                Remotive
              </Badge>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:justify-end">
            {/* Work Mode Dropdown */}
            <div className="relative" ref={workModeRef}>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setWorkModeOpen(!workModeOpen)}
                className={`rounded-full border-border/60 ${workModeFilter.size > 0 ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30 hover:bg-cyan-500/20' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
              >
                Work mode <ChevronDown className="ml-1 w-3 h-3 opacity-70" />
              </Button>
              <AnimatePresence>
                {workModeOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    className="absolute z-50 top-full left-0 mt-2 w-48 bg-white dark:bg-slate-900 border border-border/50 rounded-xl shadow-xl overflow-hidden"
                  >
                    <div className="p-2 space-y-1">
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => {
                          const newFilter = new Set(workModeFilter);
                          if (newFilter.has('remote')) newFilter.delete('remote');
                          else newFilter.add('remote');
                          setWorkModeFilter(newFilter);
                        }}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${workModeFilter.has('remote') ? 'bg-cyan-500 border-cyan-500 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
                          {workModeFilter.has('remote') && <Check className="w-3 h-3" />}
                        </div>
                        <span className="text-sm font-medium">Remote</span>
                      </div>
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => {
                          const newFilter = new Set(workModeFilter);
                          if (newFilter.has('onsite')) newFilter.delete('onsite');
                          else newFilter.add('onsite');
                          setWorkModeFilter(newFilter);
                        }}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${workModeFilter.has('onsite') ? 'bg-cyan-500 border-cyan-500 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
                          {workModeFilter.has('onsite') && <Check className="w-3 h-3" />}
                        </div>
                        <span className="text-sm font-medium">On-site / Hybrid</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Date Posted Dropdown */}
            <div className="relative" ref={datePostedRef}>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDatePostedOpen(!datePostedOpen)}
                className={`rounded-full border-border/60 ${datePostedFilter !== 'any' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30 hover:bg-cyan-500/20' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
              >
                Date posted <ChevronDown className="ml-1 w-3 h-3 opacity-70" />
              </Button>
              <AnimatePresence>
                {datePostedOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    className="absolute z-50 top-full left-0 mt-2 w-48 bg-white dark:bg-slate-900 border border-border/50 rounded-xl shadow-xl overflow-hidden"
                  >
                    <div className="p-2 space-y-1">
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => { setDatePostedFilter('any'); setDatePostedOpen(false); }}
                      >
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${datePostedFilter === 'any' ? 'border-cyan-500 border-[4px]' : 'border-slate-300 dark:border-slate-600'}`} />
                        <span className="text-sm font-medium">Any time</span>
                      </div>
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => { setDatePostedFilter('month'); setDatePostedOpen(false); }}
                      >
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${datePostedFilter === 'month' ? 'border-cyan-500 border-[4px]' : 'border-slate-300 dark:border-slate-600'}`} />
                        <span className="text-sm font-medium">Past month</span>
                      </div>
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => { setDatePostedFilter('week'); setDatePostedOpen(false); }}
                      >
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${datePostedFilter === 'week' ? 'border-cyan-500 border-[4px]' : 'border-slate-300 dark:border-slate-600'}`} />
                        <span className="text-sm font-medium">Past week</span>
                      </div>
                      <div 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => { setDatePostedFilter('24h'); setDatePostedOpen(false); }}
                      >
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${datePostedFilter === '24h' ? 'border-cyan-500 border-[4px]' : 'border-slate-300 dark:border-slate-600'}`} />
                        <span className="text-sm font-medium">Past 24 hours</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sort by Match % */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortByMatch(!sortByMatch)}
              className={`rounded-full border-border/60 ${sortByMatch ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30 hover:bg-cyan-500/20' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <ArrowUpDown className="mr-1.5 w-3 h-3 opacity-70" />
              Highest Match
            </Button>

            {/* View Toggle */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800/50 rounded-full p-0.5 border border-border/50 ml-1 sm:ml-2">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-full transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 pt-0 custom-scrollbar" ref={listContainerRef}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-4" />
            <p className="text-sm text-muted-foreground font-medium animate-pulse">Scanning live opportunities...</p>
          </div>
        ) : (
          <>
            <div className={viewMode === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-12" : "flex flex-col gap-4 mb-12"}>
              <AnimatePresence mode="popLayout">
                {filteredJobs.map((job, i) => {
                  const isFavorite = favorites.has(job.id);
                  const isSaving = savingFav === job.id;

                  if (viewMode === 'list') {
                    return (
                      <JobListItem job={job} isSaving={isSaving} isFavorite={isFavorite} toggleFavorite={toggleFavorite} onTailor={onTailor} setSelectedJob={setSelectedJob} />
                    );
                  }

                  // Grid View Render
                  return (
                    <motion.div
                      key={job.id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => setSelectedJob(job)}
                      className="group bg-white dark:bg-slate-900/50 border border-border hover:border-cyan-500/50 rounded-2xl p-6 transition-all shadow-sm hover:shadow-xl relative overflow-hidden cursor-pointer flex flex-col h-full"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-cyan-500/10 transition-colors" />
                      
                      <div className="flex justify-between items-start mb-4 relative z-10 shrink-0">
                        <CompanyLogo logoUrl={job.logo_url} companyName={job.company} size="md" />
                        <div className="flex flex-col items-end gap-1.5">
                          {job.relevance_score > 0 ? (
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 text-[10px] font-bold uppercase tracking-wider">
                              <Sparkles className="w-3 h-3" />
                              {job.relevance_score}% Match
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-500/10 border border-slate-500/20 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                              No CV Uploaded
                            </div>
                          )}
                          {job.source && SOURCE_CONFIG[job.source] && (
                            <div className={`px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wider ${SOURCE_CONFIG[job.source].className}`}>
                              {SOURCE_CONFIG[job.source].label}
                            </div>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            disabled={isSaving}
                            onClick={(e) => toggleFavorite(job, e)}
                            className={`h-8 w-8 transition-colors ${isFavorite ? 'text-cyan-500 bg-cyan-500/10' : 'text-muted-foreground hover:text-cyan-500'}`}
                          >
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 mb-4 z-10 relative">
                        <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-2 group-hover:text-cyan-500 transition-colors">
                          {job.job_title}
                        </h3>
                        <div className="flex flex-col gap-2 text-xs text-muted-foreground">
                          {job.location && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 shrink-0" />
                              <span className="truncate">{job.location}</span>
                            </span>
                          )}
                          {job.salary_min && (
                            <div className="font-bold text-cyan-600 dark:text-cyan-400 mt-1">
                              {(job.salary_min > 500) ? '£' : ''}{job.salary_min.toLocaleString()} - {job.salary_max?.toLocaleString()}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 mt-1 opacity-80">
                            <span className="truncate">Posted {new Date(job.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground mb-6 line-clamp-3 leading-relaxed relative z-10">
                        {job.description}
                      </p>

                      <div className="flex items-center gap-2 relative z-10">
                        <Button 
                          onClick={(e) => {
                            e.stopPropagation();
                            onTailor(job);
                          }}
                          className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-bold rounded-xl text-[10px] uppercase tracking-widest h-10"
                        >
                          Tailor CV
                          <ArrowRight className="w-3.5 h-3.5 ml-2" />
                        </Button>
                        <Button 
                          variant="outline" 
                          className="flex-1 border-border hover:bg-muted text-[10px] font-bold uppercase tracking-widest h-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(job.apply_url, '_blank');
                          }}
                        >
                          Go to Listing
                          <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {activeFilter === 'live' && filteredJobs.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pb-12 mt-6">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    disabled={currentPage === 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="gap-2 border-border h-10 px-4 rounded-xl hover:border-cyan-500/30 hover:text-cyan-500 transition-all font-medium"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                </div>

                <div className="flex items-center gap-1.5 py-1 px-2">
                  {getPageNumbers().map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      variant={pageNumber === currentPage ? "default" : "outline"}
                      disabled={loading}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`w-10 h-10 p-0 rounded-xl transition-all font-bold ${
                        pageNumber === currentPage 
                          ? "bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-transparent shadow-md shadow-cyan-500/10" 
                          : "border-border hover:border-cyan-500/30 hover:text-cyan-500 dark:hover:text-cyan-400 bg-white/50 dark:bg-slate-900/50"
                      }`}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    disabled={!hasMore || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="gap-2 border-border h-10 px-4 rounded-xl hover:border-cyan-500/30 hover:text-cyan-500 transition-all font-medium"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {!loading && jobs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6">
              {activeFilter === 'saved' ? <Heart className="w-10 h-10 text-slate-300" /> : <Search className="w-10 h-10 text-slate-300" />}
            </div>
            <h3 className="text-xl font-bold mb-2">{activeFilter === 'saved' ? "No saved jobs yet" : "No jobs found"}</h3>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">
              {activeFilter === 'saved' ? "Bookmark jobs from the live feed to see them here." : "Try adjusting your search or updating your master CV."}
            </p>
          </div>
        )}

        {!loading && jobs.length > 0 && filteredJobs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6">
              <Search className="w-10 h-10 text-slate-300 animate-pulse" />
            </div>
            <h3 className="text-xl font-bold mb-2">No jobs from {selectedSource === 'all' ? 'any source' : SOURCE_CONFIG[selectedSource]?.label}</h3>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">
              No jobs from this source matched your criteria. Try choosing another source or resetting the filter.
            </p>
            <Button 
              variant="outline" 
              onClick={() => setSelectedSource('all')}
              className="mt-4 border-border text-cyan-500 hover:text-cyan-600 rounded-full"
            >
              Show All Sources
            </Button>
          </div>
        )}
      </div>

      {/* Full Job Details Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedJob(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-900 border border-border rounded-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl custom-scrollbar flex flex-col"
            >
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-4 right-4 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                onClick={() => setSelectedJob(null)}
              >
                <X className="w-5 h-5" />
              </Button>

              <div className="flex items-start sm:items-center gap-4 mb-6 pr-8">
                <CompanyLogo logoUrl={selectedJob.logo_url} companyName={selectedJob.company} size="lg" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold mb-2 truncate">{selectedJob.job_title}</h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium"><Building2 className="w-4 h-4 shrink-0" /> <span className="truncate">{selectedJob.company}</span></span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 shrink-0" /> <span className="truncate">{selectedJob.location}</span></span>
                    {selectedJob.salary_min && (
                      <span className="flex items-center gap-1.5 font-bold text-cyan-500">
                        {(selectedJob.salary_min > 500) ? '£' : ''}{selectedJob.salary_min.toLocaleString()} - {selectedJob.salary_max?.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center flex-wrap gap-2 mb-8">
                {selectedJob.relevance_score > 0 ? (
                  <Badge className="bg-cyan-500/10 text-cyan-500 border-cyan-500/20 px-3 py-1">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    {selectedJob.relevance_score}% Match
                  </Badge>
                ) : (
                  <Badge className="bg-slate-500/10 text-slate-500 border-slate-500/20 px-3 py-1">
                    No CV Uploaded
                  </Badge>
                )}
                {selectedJob.source && SOURCE_CONFIG[selectedJob.source] && (
                  <Badge className={`px-3 py-1 border ${SOURCE_CONFIG[selectedJob.source].className}`}>
                    {SOURCE_CONFIG[selectedJob.source].label}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground font-medium">
                  Posted {new Date(selectedJob.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="prose prose-sm dark:prose-invert max-w-none mb-8 text-muted-foreground leading-relaxed whitespace-pre-wrap flex-1">
                <h3 className="text-lg font-bold text-foreground mb-3">Job Overview</h3>
                {selectedJob.description}
                
                <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                   <p className="text-amber-600 dark:text-amber-400 text-xs m-0 font-medium flex items-start gap-2">
                     <span className="shrink-0 mt-0.5">ℹ️</span>
                     Note: Job boards often provide a truncated preview of the description via API. Click "Read Full Details & Go to Listing" to view the complete, original job posting.
                   </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-6 border-t border-border mt-auto shrink-0">
                <Button 
                  onClick={() => {
                    onTailor(selectedJob);
                    setSelectedJob(null);
                  }}
                  className="w-full sm:flex-1 bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-bold rounded-xl h-12"
                >
                  Tailor CV for this Job
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full sm:flex-1 border-border hover:bg-muted font-bold rounded-xl h-12"
                  onClick={() => window.open(selectedJob.apply_url, '_blank')}
                >
                  Read Full Details & Go to Listing
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
